import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import Lightbox from "./Lightbox";
import { EASE } from "@/lib/site";

const SPECS = [
  { v: "3-BHK + Servant", l: "Premium Golf-Facing Residence" },
  { v: "2,350 sq.ft", l: "Compact, Fully Optimised" },
  { v: "₹45 Lakhs", l: "Strategic Budget Cap" },
  { v: "4 Months", l: "Absolute Handover" },
];

const ROOMS = [
  {
    id: "drawing-room",
    label: "Drawing Room",
    name: "The Drawing Room Lounge",
    subtitle: "Bespoke Verticality & Rhythm",
    copy: [
      "Custom geometric relief — a floor-to-ceiling feature wall with a custom-molded circular pattern and clean vertical lines that lift the ceiling height.",
      "Tactile seating balance — structured, low-profile grey sofas paired with high-end patterned geometric accent chairs.",
      "Graphic light play — modern horizontal zebra blinds casting elegant lines of light and shadow.",
    ],
    images: ["drawing-room-1", "drawing-room-2", "living-1"],
  },
  {
    id: "mandir",
    label: "Mandir Niche",
    name: "Textural Backdrops & The Sacred Niche",
    subtitle: "Ambient Layers & Spiritual Sanctuary",
    copy: [
      "Sculptural stone panelling — backlit stone blocks, deep-cocoa wood bands and custom gold-disc sconces.",
      "The premium mandir — a highly detailed, open temple sanctuary framed with custom marble, gold iconography and built-in storage.",
    ],
    images: ["mandir", "sacred-art"],
  },
  {
    id: "partition",
    label: "Bronze Partition",
    name: "The Moving Metallic Boundary",
    subtitle: "Bronze Mesh Artistry",
    copy: [
      "A premium sliding door system engineered with slim brass profiles and mesh-textured bronze glass panels.",
      "Instant acoustic and visual semi-privacy when closed — glowing beautifully under direct light.",
      "Glides effortlessly away to combine the drawing room and dining suite into a single space for hosting large groups.",
    ],
    images: ["bronze-mesh", "seamless-1"],
  },
  {
    id: "dining",
    label: "Dining Suite",
    name: "The Dining Suite & Kitchen Portal",
    subtitle: "High-End Entertaining",
    copy: [
      "Grounded by a high-gloss dark table set and deep black velvet high-back chairs.",
      "The Marble Gateway — a high-vein marble architrave wrapping the kitchen entrance to frame views of the sleek interior.",
      "Dining walls lined with subtle patterned wallpaper that catches the ambient ceiling light.",
    ],
    images: ["dining-suite", "dining-1"],
  },
  {
    id: "kitchen",
    label: "Kitchen",
    name: "The Kitchen",
    subtitle: "From Closed to Effortless",
    copy: [
      "Transformed from a closed, restrictive area into an open, highly efficient workspace.",
      "Maximum storage and seamless integration of all appliances within a compact footprint.",
    ],
    images: ["kitchen-1", "kitchen-2"],
  },
  {
    id: "bedroom",
    label: "Master Bedroom",
    name: "The Master Bedroom",
    subtitle: "Calm, Minimal, Storage-Efficient",
    copy: [
      "Calm, minimal and storage-efficient — focused on relaxation.",
      "Storage without visual clutter, carried through the home's private wing.",
    ],
    images: ["bedroom-1", "bedroom-nook-1"],
  },
  {
    id: "washroom",
    label: "Master Washroom",
    name: "The Master Washroom",
    subtitle: "Luxury Redefined",
    copy: [
      "Gold-finished fittings with semi-gloss, minimal finishes.",
      "A private suite that carries the home's warm metallic language into the bath.",
    ],
    images: ["washroom-1", "bath-tub-1"],
  },
  {
    id: "study",
    label: "Study + Murphy Bed",
    name: "Study Room with Murphy Bed",
    subtitle: "Clean Lines & Clutter-Free Planning",
    copy: [
      "Clean lines and clutter-free planning with bay-window seating.",
      "A fold-away murphy bed keeps the study flexible for guests.",
    ],
    images: ["study-1", "study-shelving"],
  },
  {
    id: "art",
    label: "Art & Entrance",
    name: "Art Foyer & Main Entrance",
    subtitle: "Introduced by Art",
    copy: [
      "“Before the home is experienced, it is introduced by art” — an original Hussain greets the foyer.",
      "The main door entrance sets the tone: warm timber, brass detailing, first light.",
    ],
    images: ["hussain-art-1", "entrance-1", "entrance-2"],
  },
];

function BeforeAfter() {
  const [pos, setPos] = useState(58);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="knights-court-before-after-slider"
      className="relative h-[400px] sm:h-[480px] lg:h-[560px] overflow-hidden select-none cursor-ew-resize touch-none group"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerLeave={() => (dragging.current = false)}
    >
      <img
        src="/images/living-2.jpg"
        alt="Knights Court after handover"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src="/images/raw-2.jpg"
          alt="Raw site before The Studio31 renovation"
          className="w-full h-full object-cover"
          draggable={false}
        />
        <span className="absolute top-5 left-5 bg-ink/85 text-cream text-[10px] font-display tracking-widest2 uppercase px-3 py-2">
          Raw Site
        </span>
      </div>
      <span className="absolute top-5 right-5 bg-brass text-ink text-[10px] font-display tracking-widest2 uppercase px-3 py-2">
        After Handover
      </span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="h-full w-px bg-cream/90 -translate-x-1/2" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-cream border border-brass flex items-center justify-center shadow-xl transition-transform group-active:scale-95">
          <ArrowLeftRight size={16} className="text-ink" />
        </div>
      </div>
    </div>
  );
}

export default function KnightsCourt() {
  const [active, setActive] = useState(0);
  const [lb, setLb] = useState(null);
  const room = ROOMS[active];

  const openLightbox = (i) =>
    setLb({
      images: room.images.map((img) => ({
        src: `/images/${img}.jpg`,
        alt: `${room.name} — Knights Court by The Studio31`,
      })),
      index: i,
    });

  return (
    <section id="knights-court" data-testid="knights-court-project-section" className="bg-ink text-cream py-24 lg:py-32 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute top-8 -right-10 font-serif text-[22vw] leading-none text-white/[0.03] select-none pointer-events-none"
      >
        KC
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <FadeUp>
          <Eyebrow light>Signature Project · Best Apartment Design 2026 Entry</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-5">
            <h2 data-testid="knights-court-header" className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-none">
              Knights Court
            </h2>
            <p className="text-sm text-cream/50 tracking-wide">Sector-128, Noida</p>
          </div>
          <p className="font-serif italic text-lg lg:text-xl text-cream/70 mt-5 max-w-2xl">
            A masterclass in layered luxury and spatial fluidity — minimal pastel luxury that feels
            open, calming &amp; highly functional.
          </p>
        </FadeUp>

        <FadeUp delay={0.08}>
          <div data-testid="knights-court-specs-grid" className="grid grid-cols-2 lg:grid-cols-4 border border-white/10 mt-12">
            {SPECS.map((s, i) => (
              <div key={s.l} className={`p-6 lg:p-8 ${i > 0 ? "border-l border-white/10" : ""} ${i > 1 ? "max-lg:border-t max-lg:border-white/10" : ""} ${i === 2 ? "max-lg:border-l-0" : ""}`}>
                <div className="font-serif text-xl lg:text-2xl text-brass">{s.v}</div>
                <div className="text-[10.5px] tracking-wider text-cream/45 uppercase mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.12}>
          <p className="mt-8 text-sm text-cream/55 max-w-2xl leading-relaxed">
            <span className="text-brass font-medium">Client brief —</span> a modern, open &amp;
            highly functional home within a compact layout. Key requirements: maximum storage,
            openness &amp; seamless integration of all appliances.
          </p>
        </FadeUp>

        <FadeUp delay={0.16} className="mt-12">
          <BeforeAfter />
        </FadeUp>

        <FadeUp delay={0.1}>
          <div data-testid="transformation-story-card" className="border border-brass/25 p-8 lg:p-10 mt-6">
            <p className="font-serif text-xl lg:text-2xl leading-snug text-cream/90">
              "This project demonstrates how thoughtful design can transform spatial limitations
              into opportunities — the result is a space that feels open, efficient &amp;
              emotionally connected to its users."
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.05} className="mt-20">
          <Eyebrow light>Room by Room</Eyebrow>
          <div className="flex gap-6 lg:gap-8 overflow-x-auto border-b border-white/10 mt-6 pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {ROOMS.map((r, i) => (
              <button
                key={r.id}
                data-testid={`room-tab-${r.id}`}
                onClick={() => setActive(i)}
                className={`relative pb-4 whitespace-nowrap text-[13px] tracking-wide transition-colors duration-300 ${
                  active === i ? "text-brass" : "text-cream/50 hover:text-cream"
                }`}
              >
                {r.label}
                {active === i && (
                  <motion.span layoutId="room-underline" className="absolute bottom-0 left-0 right-0 h-px bg-brass" transition={{ duration: 0.4, ease: EASE }} />
                )}
              </button>
            ))}
          </div>
        </FadeUp>

        <AnimatePresence mode="wait">
          <motion.div
            key={room.id}
            data-testid={`room-panel-${room.id}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="grid lg:grid-cols-12 gap-10 mt-12"
          >
            <div className="lg:col-span-5">
              <h3 className="font-serif text-3xl lg:text-4xl leading-tight">{room.name}</h3>
              <p className="font-serif italic text-brass text-lg mt-3">{room.subtitle}</p>
              <ul className="mt-8 space-y-5">
                {room.copy.map((c, i) => (
                  <li key={i} className="flex gap-4 text-sm leading-relaxed text-cream/65">
                    <span className="h-1.5 w-1.5 rotate-45 bg-brass shrink-0 mt-2" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              {room.images.map((img, i) => (
                <button
                  key={img}
                  data-testid={`room-image-${img}`}
                  onClick={() => openLightbox(i)}
                  className={`group relative overflow-hidden cursor-zoom-in ${i === 0 ? "col-span-2 h-64 lg:h-80" : "h-48 lg:h-60"} ${room.images.length === 3 && i === 2 ? "max-lg:col-span-2" : ""}`}
                >
                  <img
                    src={`/images/${img}.jpg`}
                    alt={`${room.name} — Knights Court by The Studio31`}
                    className={`w-full h-full object-cover img-editorial transition-transform duration-[1.1s] ease-out group-hover:scale-105 ${i === 0 ? "" : "max-lg:h-full"}`}
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/15 transition-colors duration-300" />
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <Lightbox
          images={lb ? lb.images : []}
          index={lb ? lb.index : null}
          onClose={() => setLb(null)}
          onNavigate={(i) => setLb((cur) => (cur ? { ...cur, index: i } : cur))}
        />
      </div>
    </section>
  );
}
